import { ref } from "vue";
import { defineStore } from "pinia";
import type { SendLogParams } from "@haskoning/usage-monitor-client";

import { callApi } from "@/api/apiInteraction";
import { OpenApiExcelFunctionConstructor } from "@/api/openApiExcelFunctionConstructor";
import { usageMonitor } from "@/api/usageMonitor";
import { useApi } from "@/api/useApi";
import { constructExcelRequestItem } from "@/excel/constructExcelRequestItem";
import { setJsonAsTable } from "@/excel/excelOperations";
import { constructUrl, standardizeDecimalSeparator, validateAndFilterAddresses } from "@/utils/utils";

import {
  type ApiEntry,
  ApiMethod,
  type ApiSchema,
  type ExcelFunction,
  type InventoryItem,
} from "@/stores/types/api.types";

export const useApiStore = defineStore("api", () => {
  const baseUrl = ref<string>(import.meta.env.VITE_API_URL || "http://localhost:8000");
  const rawUrls = ref<(string | InventoryItem)[]>(
    JSON.parse(localStorage.getItem("urls") || '["http://localhost:8000"]')
  );
  const inventory = ref<InventoryItem[]>([]);
  const apiEntries = ref<Map<string, ApiEntry>>(new Map());
  const cachedSchemaMap = ref<Map<string, ApiSchema>>(new Map());
  const isInventoryLoading = ref<boolean>(false);
  const autoGenerateForm = ref<boolean>(false);
  const loggedFunctions = new Map<string, number>();

  function setRawUrls(newUrls: (string | InventoryItem)[]): void {
    rawUrls.value = newUrls;
    localStorage.setItem("urls", JSON.stringify(newUrls));
  }

  async function loadInventory(): Promise<void> {
    isInventoryLoading.value = true;

    try {
      const inventoryApi = useApi(baseUrl.value);
      const inventoryResponse: InventoryItem[] = await inventoryApi.service("inventory").get();
      inventory.value = inventoryResponse;
    } catch (err: any) {
      console.error("Failed to load API inventory:", err);
    } finally {
      isInventoryLoading.value = false;
    }
  }

  function createApiEntry(url: string, item: string | InventoryItem): ApiEntry {
    return {
      url,
      item,
      state: "idle",
      error: null,
      schemaInfo: null,
      functions: [],
      registrableFunctions: [],
      nonRegistrableFunctions: [],
      failedEndpoints: [],
    };
  }

  async function loadApiSchema(url: string): Promise<void> {
    const entry = apiEntries.value.get(url);
    if (!entry) return;

    // Update entry to loading state
    apiEntries.value.set(url, { ...entry, state: "loading", error: null });

    try {
      let apiSchema: ApiSchema;

      if (cachedSchemaMap.value.has(url)) {
        apiSchema = cachedSchemaMap.value.get(url)!;
      } else {
        const api = useApi(url);
        apiSchema = await api.service("/openapi.json").get();
        cachedSchemaMap.value.set(url, apiSchema);
      }

      // Generate functions for this API
      const constructor = new OpenApiExcelFunctionConstructor(apiSchema, url);
      const generatedFunctions = constructor.construct();

      // Update entry with schema info and functions
      const updatedEntry: ApiEntry = {
        ...entry,
        state: "loaded",
        error: null,
        schemaInfo: apiSchema.info,
        functions: generatedFunctions,
        failedEndpoints: constructor.notSupportedApiEndpoints,
        registrableFunctions: [],
        nonRegistrableFunctions: [],
      };

      apiEntries.value.set(url, updatedEntry);

      // Validate which functions can be registered with Excel and update the entry accordingly
      if (generatedFunctions.length > 0) {
        await validateFunctionsForEntry(url, generatedFunctions);
      }
    } catch (err: any) {
      console.error(`Failed to load schema for ${url}:`, err);
      apiEntries.value.set(url, {
        ...entry,
        state: "error",
        error: err.message || `Failed to load schema for ${url}`,
      });
    }
  }

  async function validateFunctionsForEntry(url: string, excelFunctions: ExcelFunction[]): Promise<void> {
    const entry = apiEntries.value.get(url);
    if (!entry) return;

    const registrable: ExcelFunction[] = [];
    const nonRegistrable: ExcelFunction[] = [];

    try {
      // Make sure Office.js is initialized
      await Office.addin.showAsTaskpane();

      for (const func of excelFunctions) {
        try {
          await (Excel as any).CustomFunctionManager.register(JSON.stringify({ functions: [func] }), "");
          registrable.push(func);
        } catch (error: any) {
          console.error(`Error validating function ${func.id}:`, error);
          nonRegistrable.push(func);
        }
      }

      // Update entry with validated results
      apiEntries.value.set(url, {
        ...entry,
        registrableFunctions: registrable,
        nonRegistrableFunctions: nonRegistrable,
      });
    } catch (error: any) {
      console.error("Error validating custom functions:", error);
    }
  }

  function buildUsageMonitorLog(func: ExcelFunction): SendLogParams {
    const apiEntry = Array.from(apiEntries.value.values()).find((entry) => func.url.startsWith(entry.url));
    const endpointPath = new URL(func.url).pathname;
    const apiName =
      typeof apiEntry?.item === "object" ? apiEntry.item.name : apiEntry?.schemaInfo?.title || "Unknown API";

    return {
      projectCode: func.id, // TODO: Consider using a more specific code if available
      projectName: apiName, // TODO: Consider using a more specific name if available
      action: `${String(func.method).toUpperCase()} ${endpointPath}`,
      applicationName: `Equation Platform - Excel Client - ${apiName}`,
    };
  }

  function sendUsageMonitorLog(func: ExcelFunction): void {
    usageMonitor.sendLog(buildUsageMonitorLog(func)).catch((usageMonitorError) => {
      console.error("Failed to send usage monitor log:", usageMonitorError);
    });
  }

  function associateFunction(func: ExcelFunction): void {
    CustomFunctions.associate(func.id, async (...args: any[]) => {
      try {
        // Last argument is the invocation context
        const invocation = args[args.length - 1];
        args.pop();

        // Standardize decimal separators in arguments
        const processedArgs = args.map((arg, index) =>
          standardizeDecimalSeparator(arg, func.parameters[index].schemaType)
        );

        // Construct the request item from Excel input
        const requestItem = constructExcelRequestItem(processedArgs, func.parameters);

        // Call the API
        let responseItem = await callApi(func.url, requestItem, func.method as ApiMethod);

        // Handle object responses by setting as Excel table
        if (typeof responseItem === "object") {
          responseItem = await setJsonAsTable(
            responseItem,
            invocation.address,
            validateAndFilterAddresses(invocation.parameterAddresses)
          );
        }

        const THROTTLE_WINDOW = 5 * 60 * 1000;
        const now = Date.now();
        const lastLogged = loggedFunctions.get(func.id) || 0;

        if (now - lastLogged > THROTTLE_WINDOW) {
          loggedFunctions.set(func.id, now);
          sendUsageMonitorLog(func);
        }

        return responseItem;
      } catch (error: any) {
        console.error(`Error calling API for function ${func.id}:`, error);
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, `Error: ${error.message}`);
      }
    });
  }

  async function registerAllFunctions(): Promise<void> {
    const allRegistrable: ExcelFunction[] = [];

    apiEntries.value.forEach((entry) => {
      allRegistrable.push(...entry.registrableFunctions);
    });

    try {
      // Make sure Office.js is initialized
      await Office.addin.showAsTaskpane();

      // Register the complete set of functions (replaces any previous registration)
      await (Excel as any).CustomFunctionManager.register(JSON.stringify({ functions: allRegistrable }), "");

      // Associate each function with its implementation
      allRegistrable.forEach((func) => associateFunction(func));
    } catch (error: any) {
      console.error("Error registering all custom functions:", error);
    }
  }

  async function syncUrls(newUrls: (string | InventoryItem)[]): Promise<void> {
    const newUrlsSet = new Set(
      newUrls.map((item) =>
        typeof item === "object" && item !== null ? constructUrl(baseUrl.value, item.path) : String(item)
      )
    );

    // Remove APIs that are no longer in the list
    const toRemove: string[] = [];
    apiEntries.value.forEach((_, url) => {
      if (!newUrlsSet.has(url)) {
        toRemove.push(url);
      }
    });
    toRemove.forEach((url) => apiEntries.value.delete(url));

    // Add new APIs and load their schemas
    const addPromises: Promise<void>[] = [];
    newUrls.forEach((item) => {
      const url = typeof item === "object" && item !== null ? constructUrl(baseUrl.value, item.path) : String(item);
      if (!apiEntries.value.has(url)) {
        apiEntries.value.set(url, createApiEntry(url, item));
        addPromises.push(loadApiSchema(url));
      }
    });

    await Promise.all(addPromises);

    await registerAllFunctions();
  }

  return {
    baseUrl,
    rawUrls,
    inventory,
    apiEntries,
    isInventoryLoading,
    autoGenerateForm,
    setRawUrls,
    loadInventory,
    syncUrls,
  };
});
